<script setup>
import { ref, shallowRef, onMounted, onBeforeUnmount, watch } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorSwatchPicker.vue';
import htmlToMarkdownConverter from 'dashboard/routes/dashboard/settings/labels/HtmlToMarkdownConverter.js';
import MarkdownIt from 'markdown-it';

// in MB

const props = defineProps({
  modelValue: { type: String, default: '' },
  showToolbar: { type: Boolean, default: false },
  showColorPicker: { type: Boolean, default: true },
  showImageUpload: { type: Boolean, default: true },
  minHeight: { type: String, default: '12rem' },
  maxHeight: { type: String, default: '24rem' },
  variables: { type: Array, default: () => [] },
  outputFormat: { type: String, default: 'html' },
  variableFormat: { type: String, default: 'single' },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);
const MAXIMUM_FILE_UPLOAD_SIZE = 4;
const { t } = useI18n();
const linkUrl = ref('');
const showLinkInput = ref(false);
const showVariableDropdown = ref(false);
const variableDropdownRef = ref(null);
const variableTriggerRef = ref(null);
const imageUploadInput = ref(null);
const editor = shallowRef(null);
const md = new MarkdownIt();
let isUpdatingFromProp = false;

function toEditorHtml(value) {
  if (props.outputFormat === 'markdown' && value) return md.render(value);
  return value || '';
}

function createEditor() {
  const extensions = [StarterKit];

  if (props.showToolbar) {
    extensions.push(
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Underline,
      Image.configure({ inline: true, allowBase64: false })
    );
    if (props.showColorPicker) {
      extensions.push(TextStyle, Color);
    }
  }

  editor.value = new Editor({
    content: toEditorHtml(props.modelValue),
    extensions,
    onUpdate: ({ editor: ed }) => {
      if (!isUpdatingFromProp) {
        let output;
        if (props.outputFormat === 'text') output = ed.getText();
        else if (props.outputFormat === 'markdown')
          output = htmlToMarkdownConverter.convertToMarkdown(ed.getHTML());
        else output = ed.getHTML();
        emit('update:modelValue', output);
      }
    },
    editorProps: {
      attributes: {
        style: `min-height: ${props.minHeight}; max-height: ${props.maxHeight}`,
        ...(props.placeholder ? { 'data-placeholder': props.placeholder } : {}),
      },
    },
  });
}

function getEditor() {
  return editor.value;
}

function setContent(content, emitUpdate = false) {
  if (!editor.value) return;
  isUpdatingFromProp = true;
  editor.value.commands.setContent(content, emitUpdate);
  isUpdatingFromProp = false;
}

watch(
  () => props.modelValue,
  newVal => {
    if (editor.value && newVal !== editor.value.getHTML()) {
      setContent(toEditorHtml(newVal));
    }
  }
);

// Toolbar actions
function toggleBold() {
  editor.value.chain().focus().toggleBold().run();
}
function toggleItalic() {
  editor.value.chain().focus().toggleItalic().run();
}
function toggleUnderline() {
  editor.value.chain().focus().toggleUnderline().run();
}
function toggleStrike() {
  editor.value.chain().focus().toggleStrike().run();
}
function toggleBulletList() {
  editor.value.chain().focus().toggleBulletList().run();
}
function toggleOrderedList() {
  editor.value.chain().focus().toggleOrderedList().run();
}
function toggleHeading(level) {
  editor.value.chain().focus().toggleHeading({ level }).run();
}
function toggleLinkInput() {
  if (editor.value.isActive('link')) {
    editor.value.chain().focus().unsetLink().run();
    showLinkInput.value = false;
    linkUrl.value = '';
    return;
  }
  const existing = editor.value.getAttributes('link').href;
  linkUrl.value = existing || '';
  showLinkInput.value = !showLinkInput.value;
}
function applyLink() {
  if (!linkUrl.value) {
    editor.value.chain().focus().unsetLink().run();
  } else {
    editor.value
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: linkUrl.value })
      .run();
  }
  showLinkInput.value = false;
  linkUrl.value = '';
}
function applyLinkOnEnter(event) {
  if (event.key === 'Enter') {
    event.preventDefault();
    applyLink();
  }
}
function openImageUpload() {
  imageUploadInput.value?.click();
}

function onColorSelect(color) {
  editor.value.chain().focus().setColor(color).run();
}
function onColorReset() {
  editor.value.chain().focus().unsetColor().run();
}

function toggleVariableDropdown() {
  showVariableDropdown.value = !showVariableDropdown.value;
}
function insertVariable(variable) {
  const wrapped =
    props.variableFormat === 'liquid'
      ? `{{${variable.name}}}`
      : `{${variable.name}}`;
  editor.value.chain().focus().insertContent(wrapped).run();
  showVariableDropdown.value = false;
}
function variableDisplayText(variable) {
  return props.variableFormat === 'liquid'
    ? `{{${variable.name}}}`
    : `{${variable.name}}`;
}
function onVariableClickOutside(event) {
  if (
    showVariableDropdown.value &&
    variableDropdownRef.value &&
    !variableDropdownRef.value.contains(event.target) &&
    variableTriggerRef.value &&
    !variableTriggerRef.value.contains(event.target)
  ) {
    showVariableDropdown.value = false;
  }
}
async function onImageFileChange() {
  const file = imageUploadInput.value?.files?.[0];
  if (!file) return;

  if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
    useAlert(
      t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SIZE_ERROR',
        { size: MAXIMUM_FILE_UPLOAD_SIZE }
      )
    );
    imageUploadInput.value.value = '';
    return;
  }

  try {
    const { fileUrl } = await uploadFile(file);
    if (fileUrl) {
      editor.value.chain().focus().setImage({ src: fileUrl }).run();
    }
    useAlert(
      t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SUCCESS')
    );
  } catch {
    useAlert(
      t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_ERROR')
    );
  }

  imageUploadInput.value.value = '';
}

onMounted(() => {
  createEditor();
  document.addEventListener('click', onVariableClickOutside);
});

onBeforeUnmount(() => {
  editor.value?.destroy();
  document.removeEventListener('click', onVariableClickOutside);
});

defineExpose({ getEditor, setContent });
</script>

<template>
  <div
    class="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
  >
    <!-- Formatting Toolbar -->
    <div
      v-if="showToolbar && editor"
      class="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex-wrap rounded-t-lg"
    >
      <button
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :class="{
          'bg-slate-200 dark:bg-slate-700 text-n-brand':
            editor.isActive('bold'),
        }"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.BOLD')"
        @click="toggleBold"
      >
        <span class="i-lucide-bold size-4" />
      </button>
      <button
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :class="{
          'bg-slate-200 dark:bg-slate-700 text-n-brand':
            editor.isActive('italic'),
        }"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.ITALIC')"
        @click="toggleItalic"
      >
        <span class="i-lucide-italic size-4" />
      </button>
      <button
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :class="{
          'bg-slate-200 dark:bg-slate-700 text-n-brand':
            editor.isActive('underline'),
        }"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.UNDERLINE')"
        @click="toggleUnderline"
      >
        <span class="i-lucide-underline size-4" />
      </button>
      <button
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :class="{
          'bg-slate-200 dark:bg-slate-700 text-n-brand':
            editor.isActive('strike'),
        }"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.STRIKETHROUGH')"
        @click="toggleStrike"
      >
        <span class="i-lucide-strikethrough size-4" />
      </button>

      <div class="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

      <button
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :class="{
          'bg-slate-200 dark:bg-slate-700 text-n-brand': editor.isActive(
            'heading',
            { level: 3 }
          ),
        }"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.HEADING')"
        @click="toggleHeading(3)"
      >
        <span class="i-lucide-heading-3 size-4" />
      </button>

      <div class="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

      <button
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :class="{
          'bg-slate-200 dark:bg-slate-700 text-n-brand':
            editor.isActive('bulletList'),
        }"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.BULLET_LIST')"
        @click="toggleBulletList"
      >
        <span class="i-lucide-list size-4" />
      </button>
      <button
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :class="{
          'bg-slate-200 dark:bg-slate-700 text-n-brand':
            editor.isActive('orderedList'),
        }"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.ORDERED_LIST')"
        @click="toggleOrderedList"
      >
        <span class="i-lucide-list-ordered size-4" />
      </button>

      <div class="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

      <button
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :class="{
          'bg-slate-200 dark:bg-slate-700 text-n-brand':
            editor.isActive('link'),
        }"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.LINK')"
        @click="toggleLinkInput"
      >
        <span class="i-lucide-link size-4" />
      </button>
      <button
        v-if="showImageUpload"
        type="button"
        class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.IMAGE')"
        @click="openImageUpload"
      >
        <span class="i-lucide-image size-4" />
      </button>
      <ColorPicker
        v-if="showColorPicker"
        :active-color="editor.getAttributes('textStyle').color || ''"
        :reset-label="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.RESET_COLOR')"
        :custom-hex-placeholder="
          $t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.CUSTOM_HEX_PLACEHOLDER')
        "
        :custom-hex-apply="
          $t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.CUSTOM_HEX_APPLY')
        "
        :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.TEXT_COLOR')"
        @select="onColorSelect"
        @reset="onColorReset"
      />

      <template v-if="variables.length">
        <div class="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        <div class="relative">
          <button
            ref="variableTriggerRef"
            type="button"
            class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.VARIABLE')"
            @click="toggleVariableDropdown"
          >
            <span class="i-lucide-braces size-4" />
          </button>
          <div
            v-if="showVariableDropdown"
            ref="variableDropdownRef"
            class="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1 w-56 max-h-64 overflow-y-auto"
          >
            <button
              v-for="variable in variables"
              :key="variable.name"
              type="button"
              class="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
              @click="insertVariable(variable)"
            >
              <span
                class="text-slate-400 dark:text-slate-500 font-mono text-[10px] truncate"
              >
                {{ variableDisplayText(variable) }}
              </span>
              <span
                class="text-slate-600 dark:text-slate-300 ml-auto flex-shrink-0"
              >
                {{ variable.label }}
              </span>
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Variable-only bar (when no toolbar but variables exist) -->
    <div
      v-if="!showToolbar && variables.length && editor"
      class="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900"
    >
      <div class="relative">
        <button
          ref="variableTriggerRef"
          type="button"
          class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400"
          :title="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.VARIABLE')"
          @click="toggleVariableDropdown"
        >
          <span class="i-lucide-braces size-3.5" />
          {{ $t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.VARIABLE') }}
        </button>
        <div
          v-if="showVariableDropdown"
          ref="variableDropdownRef"
          class="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1 w-56 max-h-64 overflow-y-auto"
        >
          <button
            v-for="variable in variables"
            :key="variable.name"
            type="button"
            class="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
            @click="insertVariable(variable)"
          >
            <span
              class="text-slate-400 dark:text-slate-500 font-mono text-[10px] truncate"
            >
              {{ variableDisplayText(variable) }}
            </span>
            <span
              class="text-slate-600 dark:text-slate-300 ml-auto flex-shrink-0"
            >
              {{ variable.label }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Link URL Input -->
    <div
      v-if="showLinkInput"
      class="flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-25 dark:bg-slate-850"
    >
      <span class="i-lucide-link size-4 text-slate-400" />
      <input
        v-model="linkUrl"
        type="url"
        class="reset-base flex-1 text-sm bg-transparent border-none outline-none text-slate-800 dark:text-slate-200"
        :placeholder="$t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.LINK_PLACEHOLDER')"
        @keydown="applyLinkOnEnter"
      />
      <button
        type="button"
        class="text-xs font-medium text-n-brand hover:text-n-brand/80 transition-colors"
        @click="applyLink"
      >
        {{ $t('EMAIL_TEMPLATES.SIGNATURE_TOOLBAR.APPLY') }}
      </button>
    </div>

    <!-- Hidden image file input -->
    <input
      v-if="showImageUpload"
      ref="imageUploadInput"
      type="file"
      accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
      hidden
      @change="onImageFileChange"
    />

    <!-- Editor Content -->
    <EditorContent
      v-if="editor"
      :editor="editor"
      class="prose prose-sm max-w-none dark:prose-invert overflow-hidden rounded-b-lg"
    />
  </div>
</template>

<style lang="scss" scoped>
:deep(.ProseMirror) {
  border: none;
  outline: none;
  overflow-y: auto;
  padding: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;

  &:focus {
    outline: none;
    border: none;
  }

  p {
    margin: 0.5rem 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: #3b82f6;
    text-decoration: underline;
    cursor: pointer;
  }

  strong,
  b {
    color: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 0.25rem;
  }

  &.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
  }
}
</style>
